import * as React from 'react';
import { Component } from 'react';
import propz from 'propz';
import { Map } from 'Src/components/Map/Map';
import { TournamentConfirmationRequestMessage, MessageField } from 'Src/models/message';
import { QUESTIONS_TEMPLATE_TYPE } from 'Src/consts/message';
import { getSelectOptionForBooleanQuestion } from 'Src/helpers/table/select';
import { formatAgesCompactForSchool } from 'Src/helpers/ageGroups';
import { FormAnswersSummary } from 'Src/components/FormAnswersSummary/FormAnswersSummary';
import { FormAnswerMap, answersToMap } from 'Src/helpers/form/formRuntime';

interface Props {
  message: TournamentConfirmationRequestMessage;
  onCloseClick: () => void;
}

interface State {
  isMapVisible: boolean;
  answers: FormAnswerMap;
}

export class ConfirmationRequestViewForm extends Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = {
      isMapVisible: false,
      answers: answersToMap(props.message.formAnswers || [])
    };
  }

  toggleMap = () => {
    this.setState(prevState => ({
      isMapVisible: !prevState.isMapVisible
    }));
  };

  formatTournamentWindow = (startRaw: any, endRaw: any): string => {
    if (!startRaw) return '';

    const start = new Date(startRaw);
    if (Number.isNaN(start.getTime())) return '';

    const pad2 = (n: number) => (String(n).length >= 2 ? String(n) : `0${n}`);
    const formatDate = (d: Date) => `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
    const formatTime = (d: Date) => `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;

    if (!endRaw) return `${formatDate(start)} ${formatTime(start)}`;

    const end = new Date(endRaw);
    if (Number.isNaN(end.getTime())) return `${formatDate(start)} ${formatTime(start)}`;

    const isSameDay =
      start.getFullYear() === end.getFullYear() &&
      start.getMonth() === end.getMonth() &&
      start.getDate() === end.getDate();

    if (isSameDay) {
      return `${formatDate(start)} ${formatTime(start)} - ${formatTime(end)}`;
    }

    return `${formatDate(start)} ${formatTime(start)} - ${formatDate(end)} ${formatTime(end)}`;
  };

  getBooleanLabel = (value: any): string => {
    const options = getSelectOptionForBooleanQuestion();
    const found = options.find(option => option.value === value);

    if (found) {
      return String(found.text);
    }

    return String(typeof value !== 'undefined' && value !== null && value !== '' ? value : '—');
  };

  getFieldTypeLabel = (type: string): string => {
    switch (type) {
      case QUESTIONS_TEMPLATE_TYPE.STRING:
        return 'Text';
      case QUESTIONS_TEMPLATE_TYPE.NUMBER:
        return 'Number';
      case QUESTIONS_TEMPLATE_TYPE.BOOLEAN:
        return 'Yes / No';
      case QUESTIONS_TEMPLATE_TYPE.ENUM:
        return 'Options';
      default:
        return 'Question';
    }
  };

  getFieldValue = (field: MessageField): string => {
    if (field.type === QUESTIONS_TEMPLATE_TYPE.BOOLEAN) {
      return this.getBooleanLabel(field.value);
    }

    if (typeof field.value === 'undefined' || field.value === null || field.value === '') {
      return '—';
    }

    return String(field.value);
  };

  render() {
    const { message, onCloseClick } = this.props;
    const { isMapVisible } = this.state;

    const point = propz.get(message, ['tournamentData', 'venue', 'point']);
    const tournamentName = String(propz.get(message, ['tournamentData', 'name'], 'Tournament'));
    const schoolName = String(propz.get(message, ['schoolData', 'name'], '') || '').trim();
    const venuePostcode = String(propz.get(message, ['tournamentData', 'venue', 'postcode'], '') || '').trim();
    const deadlineRaw = propz.get(message, ['deadlineForAnswers'], null);
    const deadlineText = deadlineRaw ? new Date(deadlineRaw).toLocaleString() : '';
    const tournamentWindowText = this.formatTournamentWindow(
      propz.get(message, ['tournamentData', 'startTime'], null),
      propz.get(message, ['tournamentData', 'endTime'], null)
    );

    const ageChipText = formatAgesCompactForSchool(
      propz.get(message, ['tournamentData', 'ages'], []),
      propz.get(message, ['schoolData'], null)
    );

    const fields = Array.isArray(message.fields) ? message.fields : [];

    return (
      <div className="programmeBookingModal">
        <div className="programmeBookingModalHero mb-4">
          <div className="programmeBookingModalHeroTop">
            <div>
              <div className="programmeBookingModalEyebrow">Submitted tournament response</div>
              <div className="programmeBookingModalClubName">{tournamentName}</div>
            </div>

            <div className="programmeBookingModalBadges">
              {deadlineText ? (
                <span className="badge badge-light border mr-2 mb-2">Reply deadline: {deadlineText}</span>
              ) : null}

              {schoolName ? <span className="badge badge-light border mr-2 mb-2">{schoolName}</span> : null}

              {ageChipText ? <span className="badge badge-light border mr-2 mb-2">Ages: {ageChipText}</span> : null}
            </div>
          </div>

          <div className="programmeBookingModalMeta mt-2">
            {tournamentWindowText ? <span>Tournament window: {tournamentWindowText}</span> : null}
            {venuePostcode ? <span>Venue: {venuePostcode}</span> : null}
          </div>

          <div className="programmeBookingModalInviteNote mt-3">
            Below are the answers your school submitted for this confirmation request.
          </div>
        </div>

        {point ? (
          <div className="mb-3">
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={this.toggleMap}>
              {isMapVisible ? 'Hide map' : 'View map'}
            </button>
          </div>
        ) : null}

        {isMapVisible && point ? (
          <Map point={point} customStylingClass="mb-3 eClubWizardMap" isMarkerDraggable={false} />
        ) : null}

        <div className="programmeBookingModal">
          {message.formSchemaVersion === 2 && message.formDefinition ? (
            <FormAnswersSummary
              definition={message.formDefinition}
              answers={this.state.answers}
            />
          ) : fields.length === 0 ? (
            <div className="alert alert-light border">No submitted answers recorded.</div>
          ) : (
            fields.map((field: MessageField, index: number) => {
              const fieldType = String(field.type || '');
              const enumOptions = Array.isArray(field.enumOptions) ? field.enumOptions : [];

              return (
                <div key={`view_form_field_${index}`} className="programmeBookingQuestionCard mb-3">
                  <div className="d-flex justify-content-between align-items-start mb-2 flex-wrap" style={{ gap: 8 }}>
                    <label className="font-weight-bold mb-0">
                      {field.heading}
                      {field.isRequired ? <span className="text-danger ml-1">*</span> : null}
                    </label>

                    <div className="d-flex flex-wrap" style={{ gap: 6 }}>
                      <span className="badge badge-light border">{this.getFieldTypeLabel(fieldType)}</span>
                      <span className={`badge ${field.isRequired ? 'badge-warning' : 'badge-secondary'}`}>
                        {field.isRequired ? 'Required' : 'Optional'}
                      </span>
                      {fieldType === QUESTIONS_TEMPLATE_TYPE.ENUM ? (
                        <span className="badge badge-light border">{enumOptions.length} options</span>
                      ) : null}
                    </div>
                  </div>

                  <div
                    style={{
                      padding: '10px 12px',
                      border: '1px solid rgba(0,0,0,0.08)',
                      borderRadius: 10,
                      background: '#fff',
                      color: '#233142',
                      lineHeight: 1.45
                    }}
                  >
                    {this.getFieldValue(field)}
                  </div>

                  {fieldType === QUESTIONS_TEMPLATE_TYPE.ENUM && enumOptions.length > 0 ? (
                    <div className="small text-muted mt-2">Options: {enumOptions.join(', ')}</div>
                  ) : null}
                </div>
              );
            })
          )}
        </div>

        <div className="d-flex justify-content-end mt-4">
          <button type="button" className="btn btn-outline-secondary" onClick={onCloseClick}>
            Close
          </button>
        </div>
      </div>
    );
  }
}

